import type { OrchestratedQueryStore } from "@/domains/library"
import { IEventBus } from "@/domains/events"
import { OrchestratedQuery } from "@/domains/library"

import type { IEpochGraphQlTransport, IEpochRepository } from "./interfaces"
import type { EpochApiData } from "./types"
import { EpochOrchestrator } from "./epoch-orchestrator"

export class EpochOrchestratorFactory {
  /**
   * Create a fully configured EpochOrchestrator with its OrchestratedQueries
   */
  static create(
    epochGraphQlTransport: IEpochGraphQlTransport,
    epochRepository: IEpochRepository,
    orchestratedQueryStore: OrchestratedQueryStore,
    eventBus: IEventBus,
  ): EpochOrchestrator {
    // 1. Create orchestrator with all dependencies passed in
    const orchestrator = new EpochOrchestrator(epochRepository, epochGraphQlTransport)

    // 2. Create current epoch query (always runs, polls every minute)
    const currentEpochQuery = new OrchestratedQuery<EpochApiData | null>(
      "Current Epoch Sync",
      (query) => orchestrator.onCurrentEpochQueryTrigger(query),
      60_000, // 60 seconds (1 minute) interval
      orchestrator.onCurrentEpochQuerySuccess.bind(orchestrator),
      orchestrator.onCurrentEpochQueryFailed.bind(orchestrator),
      orchestratedQueryStore,
    )

    // 3. Create historic epochs query (only runs when started, polls every 5 minutes)
    const historicEpochsQuery = new OrchestratedQuery<EpochApiData[]>(
      "Historic Epochs Sync",
      (query) => orchestrator.onHistoricEpochsQueryTrigger(query),
      300_000, // 5 minutes interval
      orchestrator.onHistoricEpochsQuerySuccess.bind(orchestrator),
      orchestrator.onHistoricEpochsQueryFailed.bind(orchestrator),
      orchestratedQueryStore,
    )

    // 4. Initialize orchestrator with queries
    orchestrator.initialize(currentEpochQuery, historicEpochsQuery)

    // 5. Do NOT start historic query automatically - wait for page store to start it
    // Current epoch query will be started by ScaleApp initialization

    return orchestrator
  }
}
